
import { useEffect, useState } from 'react';
// @mui
import LoadingButton from '@mui/lab/LoadingButton';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import LinearProgress from '@mui/material/LinearProgress';
import Typography from '@mui/material/Typography';
import Alert from '@mui/material/Alert';
import { useTheme } from '@mui/material/styles';

type Props = {
  open: boolean;
  countdown: number; // countdown in milliseconds
  onExtendSession: VoidFunction;
  onLogoutNow: VoidFunction;
};

export default function SessionTimeoutDialog({ 
  open, 
  countdown, 
  onExtendSession, 
  onLogoutNow 
}: Props) {
  const theme = useTheme();
  const [timeLeft, setTimeLeft] = useState(0);
  const [progressValue, setProgressValue] = useState(100);

  useEffect(() => {
    if (countdown > 0) {
      setTimeLeft(Math.ceil(countdown / 1000));
      setProgressValue((countdown / (1 * 60 * 1000)) * 100); // 1 minute warning period
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [countdown]);

  const formatTime = (seconds: number) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
  };

  return (
    <Dialog
      fullWidth
      maxWidth={false}
      open={open}
      onClose={onLogoutNow}
      PaperProps={{
        sx: { maxWidth: 720 },
      }}
    >
      <DialogTitle>Session Timeout Warning</DialogTitle>

      <DialogContent>
        <Box rowGap={3} columnGap={2} display="grid" sx={{ pb: 1 }}>
          <Alert severity="warning" variant="outlined">
            Your session will expire soon due to inactivity. You will be automatically logged out in:
          </Alert>

          <Box display="grid" justifyItems="center">
            <Typography variant="h3" color="error">
              {formatTime(timeLeft)}
            </Typography>
            
            <LinearProgress
              variant="determinate"
              value={Math.max(0, progressValue)}
              color="error"
              sx={{ width: '100%', height: 8, borderRadius: 4 }}
            />
          </Box>

          <Typography variant="body2" color="text.secondary">
            Click &quot;Stay Logged In&quot; to extend your session, or &quot;Logout Now&quot; to log out immediately.
          </Typography>
        </Box>
      </DialogContent>

      <DialogActions>
        <Button variant="outlined" onClick={onLogoutNow}>
          Logout Now
        </Button>
        <LoadingButton type="submit" variant="contained" color='primary' onClick={onExtendSession}>
          Stay Logged In
        </LoadingButton>
      </DialogActions>
    </Dialog>
  )
}
